/**
 * Every string the Create User wizard shows (PD-4064), taken from the Figma flow
 * (file 1emHQYcS80Ax4DRFgB3Lak, node 787:76635).
 *
 * Kept in one place because the copy is the part of this feature most likely to
 * be revised, and because several strings are asserted in tests.
 *
 * Two deliberate departures from the mockup, both noted where they occur:
 *   - anything referring to Teams, which this ticket does not build (PD-4213)
 *   - the success panel gains the welcome-email sentence the ticket requires
 */

export const WIZARD_TITLE = 'Create User';

export const REQUIRED_FIELDS = 'Required Fields';

export const STEPS = {
  // The mockup labels this "Team & Account". PD-4064 has no Team field — the
  // grooming notes defer it — so naming the step for what it actually holds
  // avoids promising a control that is not there. PD-4213 renames it when it
  // adds the field.
  account: 'Account',
  details: 'User Details',
  permissions: 'Permissions & Discoverability',
};

export const ACCOUNT_STEP = {
  title: 'Account',
  subtitle: 'Select the accounts to assign this user to.',
  label: 'Account',
  // The mockup's help line, plus the half the mockup could leave out: with five
  // accounts drawn, "from the list" was the whole story. Against hundreds the list
  // is paged, so searching is how an account outside the loaded page gets found —
  // and nothing else on screen says the field can be typed into.
  help:
    'Choose one or multiple accounts from the list, or type to search by name.',
  // Shown while the field is empty, as the affordance for that.
  searchPlaceholder: 'Search accounts by name',
  // The chevron's own name, distinct from the field's, so the two controls inside
  // the field are separable by assistive tech and by tests.
  toggle: 'Show account options',
  // The count the admin would otherwise have no way to know, since a page cannot
  // say whether it is all of them. Neither this nor the loading line is in the
  // mockup, which draws five accounts and never reaches a second page.
  paged: (loaded, total) => `Showing ${loaded} of ${total} accounts`,
  loadingMore: 'Loading more accounts...',
  empty: 'No accounts available.',
};

export const DETAILS_STEP = {
  title: 'User Details',
  subtitle: 'Provide user information and contact details to proceed.',
  firstName: 'First Name',
  lastName: 'Last Name',
  jobRole: 'Job Role',
  jobRoleHelp:
    'This Job Role will appear by your contact name for additional context.',
  dmsId: 'DMS ID',
  dmsIdHelp:
    'User ID in your Dealer Management System - used in DMS integration modules.',
  email: 'Email',
  emailTaken:
    'This email is already associated with a user. Use a different email to continue.',
  countryCode: 'Country Code',
  mobile: 'Mobile Phone Number',
  mobileHelp: 'Required for SMS notifications.',
  mobileTaken:
    'This phone number is already associated with a user. Use a different phone number to continue.',
  // Not in the mockup, which only draws the duplicate case. The server rejects a
  // malformed number too, and saying so here beats failing at the end.
  mobileInvalid: 'Enter a valid phone number for the selected country code.',
};

export const PERMISSIONS_STEP = {
  title: 'Permissions & Discoverability',
  subtitle: 'Configure access permissions and discoverability for this user.',
  // Shown only for more than one account: it is what tells the admin that the one
  // set of permissions below applies to all of them for now.
  multiAccountNotice:
    'After the user is created, you can define permissions and discoverability settings for each account.',
  permissionsTitle: 'User Permissions',
  permissionsSubtitle: "View and manage this user's permissions.",
  // Not in the mockup, which draws all three rows already ticked. We start blank
  // instead — pre-granting Fleet Administrator is not a default to make on the
  // admin's behalf — and CREATE USER needs at least one, so the requirement has to
  // say so rather than leaving a greyed-out button with no explanation.
  permissionsRequired: 'Select at least one permission to continue.',
  discoverabilityTitle: 'Discoverability',
  discoverabilitySubtitle:
    'Specify whether to display this account owner in recipients lists for the Notes sections of a case.',
  // Matches decisiv.users.hidden_external_fleet and
  // decisiv.users.note_recipient_listed_fleet, so the wizard and the legacy edit
  // page describe the same two flags the same way.
  hiddenExternal:
    'Hide from external users (provider contacts) in the recipients lists.',
  hiddenInternal:
    'Hide from internal users and contacts (at my location) in the recipients lists.',
};

export const CREATING_PANEL = {
  title: 'Creating User...',
  // The mockup says "Applying teams, accounts, and permissions"; there are no
  // teams to apply.
  body:
    "Processing user creation. Closing this window won't interrupt it. Applying accounts and permissions.",
  action: 'Creating User...',
};

export const SUCCESS_PANEL = {
  title: 'User Created Successfully',
  body: (username) =>
    `The user (${username}) has been created and is ready to use.`,
  // Required by the ticket: the admin never sets a password, so they need to know
  // the user is contacted automatically. The mockup's body copy is placeholder
  // and does not contradict it.
  emailNotice: 'They will receive a welcome email automatically.',
  viewUser: 'View New User',
  createAnother: 'Create New User',
  close: 'Close',
};

export const ERROR_PANEL = {
  title: 'Failed to Create User',
  body:
    'User creation failed due to an unexpected error. Try again or contact support.',
  retry: 'Retry',
  close: 'Close',
};
